import { useEffect, useState } from 'react';
import { Box, Text, useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowLeftIcon from '../../assets/arrow-left.svg';
import EditSquareIcon from '../../assets/edit-square-icon.svg';
import EditDisabledIcon from '../../assets/edit-square-disabled-icon.svg';
import DoneIcon from '../../assets/done-icon.svg';
import DoneDisabledIcon from '../../assets/done-disabled-icon.svg';
import RedirectIcon from '../../assets/redirect-icon-upper.svg';
import InformIcon from '../../assets/inform-green-icon.svg';
import ArrowGreenIcon from '../../assets/arrow-green-icon.svg';

import Button from '../../components/Button';
import { TooltipComponent } from '../../components/Tooltip';
import ConfirmModal from '../../components/SubmissionContainer/modal/confirm-modal.component';
import { SubmissionContainerComponent } from '../../components/SubmissionContainer';

import { closeRequest, fetchRequestDetail } from '../../apis/request.api';
import ToastBtnMessage from '../../components/ToastBtnMessage';
import SEO from '../../components/SEO';
import { generateSEOMetadata, dynamicPages } from '../../utils/seo';

const EQUITY_PERCENTAGE_TOOLTIP = 'The percentage of equity the client has proposed.';

const ReviewSubmissions = () => {
  const { id: requestId } = useParams();
  const [request, setRequest] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [modal, setModal] = useState('');
  const [isCloseRequest, setRequestStatus] = useState(false);
  const MAXIMUM_DESCRIPTION_LENGTH = 300;
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestRes = await fetchRequestDetail(requestId);
        if (requestRes) {
          setRequest(requestRes);
          setRequestStatus(requestRes.status === 'closed' ? true : false);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);

  // Generate SEO metadata for review submissions page
  const seoMetadata = generateSEOMetadata.dynamicPage(
    dynamicPages['request/submissions'].name,
    dynamicPages['request/submissions'].url,
    dynamicPages['request/submissions'].description
  );

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleCloseModal = async () => {
    await closeRequest(requestId)
      .then(() => {
        setModal('');
        navigate(0);
      })
      .catch((e) => {
        console.log(e);
        toast({
          render: () => (
            <ToastBtnMessage
              title={'Close request failed'}
              isError={true}
              color="jet-black"
              isHiddenButton
            />
          ),
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right'
        });
      });
  };

  return (
    <div className="bg-06-seasalt-white">
      <SEO {...seoMetadata} />
      <div className="max-w-[1308px] mx-auto py-10 px-0 md:px-10">
        <div className="mb-8 px-4 md:px-0">
          <div className="mb-[10px]">
            <div className="flex items-center">
              <img
                className="p-2 mr-2 cursor-pointer"
                src={ArrowLeftIcon}
                alt=""
                onClick={() => {
                  navigate('/article/manage?tab=requests');
                }}
              />
              <p className="font-[800] text-xl md:text-2xl">Request responses</p>
            </div>
            <div className="font-[400] text-02-dim-gray">View and respond to request responses</div>
          </div>
          <div className="border border border-[#C8E6C9] bg-white rounded-[8px] p-[20px]">
            <div className="block md:flex items-center">
              <p className="font-[800] text-[18px] block mb-2 md:mb-0">Request details</p>
              <div className="md:ml-auto flex items-center">
                <Button
                  variant={isCloseRequest ? 'disable' : 'primary'}
                  className="h-9 whitespace-nowrap"
                  onClick={() => {
                    setModal('close-request');
                  }}
                >
                  Mark as Closed
                  <img
                    src={isCloseRequest ? DoneDisabledIcon : DoneIcon}
                    className="ml-2"
                    alt="Upload"
                  />
                </Button>
                <Button
                  variant={isCloseRequest ? 'disable' : 'outline'}
                  className="h-9 ml-[8px]"
                  onClick={() => {
                    if (!isCloseRequest) {
                      navigate(`/request/${request?.id}/edit`);
                    }
                  }}
                >
                  Edit
                  <img
                    src={isCloseRequest ? EditDisabledIcon : EditSquareIcon}
                    className="ml-2"
                    alt="Upload"
                  />
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <div className="font-[800] text-[16px] flex flex-wrap items-center gap-[10px]">
                <p className="truncate">{request?.title}</p>
                <a
                  href={`${process.env.REACT_APP_NEWSLETTER_URL}request/${request.id}/detail`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-growth-green-500 font-black text-sm whitespace-nowrap flex items-center gap-2"
                >
                  Open Request link
                  <img src={RedirectIcon} alt="" />
                </a>
              </div>
              <div className="flex items-center gap-[5px]">
                <span className="text-growth-green-500 text-[14px] flex items-center gap-[5px]">
                  Status:
                </span>
                <Box
                  px={'12px'}
                  rounded={'80px'}
                  bgColor={isCloseRequest ? '#4CAF50' : '#EDF7EE'}
                  w={'fit-content'}
                >
                  <Text
                    color={isCloseRequest ? '#FFFFFF' : '#204A22'}
                    fontWeight={isCloseRequest ? 800 : 600}
                    fontSize={'xs'}
                  >
                    {isCloseRequest ? 'Closed' : 'Ongoing'}
                  </Text>
                </Box>
              </div>
              <div className="flex items-center gap-[5px]">
                <span className="text-growth-green-500 text-[14px] flex items-center gap-[5px]">
                  Proposed Equity
                  <TooltipComponent description={EQUITY_PERCENTAGE_TOOLTIP} Icon={InformIcon} />:
                </span>
                <span className="text-[14px]">{request?.equityPercentage}%</span>
              </div>
              <div className="text-[14px] font-[400] text-justify relative">
                <span>
                  {request?.description?.length > MAXIMUM_DESCRIPTION_LENGTH && isCollapsed
                    ? `${request?.description.slice(0, MAXIMUM_DESCRIPTION_LENGTH)}...`
                    : request?.description}
                </span>
              </div>

              {request?.description?.length > MAXIMUM_DESCRIPTION_LENGTH && (
                <button
                  onClick={toggleCollapse}
                  className="w-fit ml-auto p-2 bg-05-seasalt-white rounded-full flex items-center justify-center"
                >
                  <img
                    src={ArrowGreenIcon}
                    className={`transition-transform duration-300 ${isCollapsed ? 'rotate-90' : 'rotate-[270deg]'}`}
                    alt="Expand/Collapse"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
        <SubmissionContainerComponent requestId={requestId} />
        {modal === 'close-request' ? (
          <ConfirmModal
            isOpen
            title="Close Request?"
            titleBtn="Confirm and Close"
            titleBtnCancel="Cancel"
            type="approve"
            desc={
              <div className="text-left">
                <p className="pb-[16px]">Closing this request will:</p>
                <ul className="list-disc space-y-2 pl-5 pb-[16px]">
                  <li>Reject all pending responses.</li>
                  <li>Deactivate the request link, making it inaccessible to others.</li>
                  <li>Allow you to view only the accepted responses.</li>
                </ul>
                <p>This action cannot be undone.</p>
              </div>
            }
            onClose={() => {
              setModal('');
            }}
            onClick={handleCloseModal}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default ReviewSubmissions;
